'use client';

import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useExitIntent } from 'use-exit-intent';

import { useAuthContext } from '@/components/Providers/AuthProvider';
import useWebAlias from '@/helpers/hooks/useWebalias';
import { useGetCartAbandonmentPopupDetailsQuery, useGetInterstitialPopupDetailsQuery } from '@/store/api/product';
import { useGetShoppingCartQuery } from '@/store/api/shoppingCart';
import { InterstitialModal } from '@/types/InterstitialModal';

import {
  hasCartPopupClosedWithoutSubmit,
  hasHomepagePopupShownThisSession,
  hasUserSubmittedDetails,
  isPopupCurrentlyOpen,
  markHomepagePopupShown,
  markPopupClosed,
  markPopupOpen,
} from './sessionStorageKeys';
import {
  isPopupWithinValidDateRange,
  normalizeCartAbandonmentModalsData,
  shouldShowInterstitialForWebalias,
} from './utils';

type InterstitialModalState = {
  showInterstitialModal: boolean;
  showSuccessModal: boolean;
  showWelcomeBackModal: boolean;
};

type InterstitialModalContextValue = {
  modals: InterstitialModalState;
  openModal: (key: keyof InterstitialModalState) => void;
  closeModal: (key: keyof InterstitialModalState) => void;
  data?: InterstitialModal;
  isCartPage: boolean;
};

export const CART_ABANDONMENT = 1;
export const HOME_PAGE_COLLECTION = 2;

const DEFAULT_STATE: InterstitialModalState = {
  showInterstitialModal: false,
  showSuccessModal: false,
  showWelcomeBackModal: false,
};

const InterstitialModalContext = createContext<InterstitialModalContextValue | null>(null);

type InterstitialProviderProps = {
  children: ReactNode;
  isCartPage: boolean;
};

export const InterstitialModalProvider = ({ children, isCartPage }: InterstitialProviderProps) => {
  // Initialize state from session storage to persist popup across page reload (from cookie consent rejection)
  const [modals, setModals] = useState<InterstitialModalState>(() => {
    if (typeof window !== 'undefined') {
      const wasPopupOpen = isPopupCurrentlyOpen();
      if (wasPopupOpen) {
        return { ...DEFAULT_STATE, showInterstitialModal: true };
      }
    }
    return DEFAULT_STATE;
  });
  const { customer, isLoading } = useAuthContext();
  const { webalias } = useWebAlias();

  // Track if exit intent handler has been registered to avoid duplicate registrations
  const exitIntentRegistered = useRef(false);

  // Fetch homepage popup data (skip for cart page or logged-in users)
  // Check customerID as customer object may always exist in new AuthProvider
  const { data } = useGetInterstitialPopupDetailsQuery(undefined, {
    skip: (!!customer?.customerID && !isLoading) || isCartPage,
  });

  // Fetch cart abandonment popup data (skip for non-cart pages or logged-in users)
  // Check customerID as customer object may always exist in new AuthProvider
  const { data: cartAbandonmentData } = useGetCartAbandonmentPopupDetailsQuery(undefined, {
    skip: (!!customer?.customerID && !isLoading) || !isCartPage,
  });

  // Get cart data to check if cart has items (only needed on cart page)
  const { totalItemsInCart } = useGetShoppingCartQuery(undefined, {
    selectFromResult: ({ data: cartData }) => ({
      totalItemsInCart: cartData?.data?.totalItemsInCart ?? 0,
    }),
    skip: !isCartPage,
  });

  const activeData = useMemo(
    () => (cartAbandonmentData ? normalizeCartAbandonmentModalsData(cartAbandonmentData) : data),
    [cartAbandonmentData, data]
  );

  // Track if cart popup impression has been sent this session (to prevent duplicate GA4 events)
  const cartImpressionSentRef = useRef(false);

  // Use refs to store latest values for exit intent handler (to avoid stale closure)
  const latestValuesRef = useRef({
    customer,
    isLoading,
    webalias,
    totalItemsInCart,
    activeData,
  });

  // Keep refs updated with latest values
  useEffect(() => {
    latestValuesRef.current = {
      customer,
      isLoading,
      webalias,
      totalItemsInCart,
      activeData,
    };
  }, [customer, isLoading, webalias, totalItemsInCart, activeData]);

  // Exit intent configuration for cart page (matches existing BeforeClose.tsx pattern)
  const { registerHandler, resetState } = useExitIntent({
    cookie: {
      daysToExpire: 0, // Session only - no persistent cookie
      key: 'interstitial-cart-exit-intent',
    },
    desktop: {
      triggerOnIdle: false,
      useBeforeUnload: false,
      triggerOnMouseLeave: true,
      delayInSecondsToTrigger: 1,
    },
    mobile: {
      triggerOnIdle: true,
      delayInSecondsToTrigger: 1,
    },
  });

  // Register exit intent handler for cart page
  // eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    // Only for cart page
    if (!isCartPage) return;

    // Avoid duplicate registrations
    if (exitIntentRegistered.current) return;
    exitIntentRegistered.current = true;

    registerHandler({
      id: 'cartAbandonmentModal',
      handler: () => {
        // Reset exit intent state to allow re-triggering if conditions change
        resetState();

        // Get latest values from ref (to avoid stale closure issues)
        const {
          customer: currentCustomer,
          isLoading: currentIsLoading,
          webalias: currentWebalias,
          totalItemsInCart: currentTotalItems,
          activeData: currentActiveData,
        } = latestValuesRef.current;

        // Don't show for logged-in users (check customerID as customer object may always exist)
        if (currentCustomer?.customerID) return;

        // Don't show if auth is still loading
        if (currentIsLoading) return;

        // Don't show for blocked webaliases (pawtree, pawtreeshop)
        if (!shouldShowInterstitialForWebalias(currentWebalias)) return;

        // Don't show if user already submitted details on any popup
        if (hasUserSubmittedDetails()) return;

        // Don't show if cart is empty
        if (currentTotalItems === 0) return;

        // Don't show if cart popup was already closed without submit in this session
        if (hasCartPopupClosedWithoutSubmit()) return;

        // Don't show if data is not loaded
        if (!currentActiveData) return;

        // Don't show if popup is outside valid date range (startDate/endDate)
        if (!isPopupWithinValidDateRange(currentActiveData.startDate, currentActiveData.endDate)) return;

        // All conditions passed - show the cart abandonment popup
        // Track popup impression for GA4 (only once per session)
        if (!cartImpressionSentRef.current && globalThis?.window?.dataLayer) {
          globalThis.window.dataLayer.push({
            event: 'popup_impression',
            popup_type: 'cart_abandonment',
            webalias: currentWebalias,
            page_url: globalThis?.window?.location?.pathname,
          });
          cartImpressionSentRef.current = true;
        }
        // Persist to session storage to survive page reload (from cookie consent rejection)
        markPopupOpen();
        setModals(s => ({ ...s, showInterstitialModal: true }));
      },
      context: ['onDesktop', 'onMobile'],
    });
  }, [isCartPage, registerHandler, resetState]);

  // Timer-based trigger for homepage/categories (non-cart pages)
  useEffect(() => {
    // Only for non-cart pages (homepage/categories)
    if (isCartPage) return;

    // Don't show for logged-in users (check customerID as customer object may always exist)
    if (customer?.customerID) return;

    // Don't show if auth is still loading
    if (isLoading) return;

    // Don't show for blocked webaliases (pawtree, pawtreeshop)
    if (!shouldShowInterstitialForWebalias(webalias)) return;

    // Don't show if data is not loaded
    if (!activeData) return;

    // Don't show if popup is outside valid date range (startDate/endDate)
    if (!isPopupWithinValidDateRange(activeData.startDate, activeData.endDate)) return;

    // Don't show if user already submitted details on any popup
    if (hasUserSubmittedDetails()) return;

    // Don't show if homepage popup was already shown in this session
    if (hasHomepagePopupShownThisSession()) return;

    // All conditions passed - show popup after timeDelay
    // API returns timeDelay in seconds, convert to milliseconds for setTimeout
    const delayInMs = activeData?.timeDelay ? activeData.timeDelay * 1000 : 3000;
    const timer = setTimeout(() => {
      markHomepagePopupShown();
      // Track popup impression for GA4
      if (globalThis?.window?.dataLayer) {
        globalThis.window.dataLayer.push({
          event: 'popup_impression',
          popup_type: 'homepage_collection',
          webalias: webalias,
          page_url: globalThis?.window?.location?.pathname,
        });
      }
      // Persist to session storage to survive page reload (from cookie consent rejection)
      markPopupOpen();
      setModals(s => ({
        ...s,
        showInterstitialModal: true,
      }));
    }, delayInMs);

    return () => clearTimeout(timer);
  }, [customer, isLoading, activeData, webalias, isCartPage]);

  const openModal = useCallback((key: keyof InterstitialModalState) => {
    setModals(s => ({ ...s, [key]: true }));
    // Persist to session storage to survive page reload (from cookie consent rejection)
    if (key === 'showInterstitialModal') {
      markPopupOpen();
    }
  }, []);

  const closeModal = useCallback((key: keyof InterstitialModalState) => {
    setModals(s => ({ ...s, [key]: false }));
    // Clear from session storage when popup is closed
    if (key === 'showInterstitialModal') {
      markPopupClosed();
    }
  }, []);

  const value = useMemo<InterstitialModalContextValue>(
    () => ({
      modals,
      openModal,
      closeModal,
      data: activeData,
      isCartPage,
    }),
    [modals, openModal, closeModal, activeData, isCartPage]
  );

  return <InterstitialModalContext.Provider value={value}>{children}</InterstitialModalContext.Provider>;
};

export const useInterstitialContext = (): InterstitialModalContextValue => {
  const context = React.useContext(InterstitialModalContext);

  if (!context) {
    throw new Error('useInterstitialContext must be used within an InterstitialModalProvider');
  }

  return context;
};
